import type { Directive } from 'vue';
import { ElMessage } from 'element-plus';
import { copyToClipboard } from '@/utils/copy';

type CopyTextGetter = () => unknown;

export interface CopyBindingOptions {
  text: unknown | CopyTextGetter;
  success?: (text: string, el: HTMLElement) => void;
  error?: (err: unknown, el: HTMLElement) => void;
  /**
   * 触发复制的事件名（默认 click）
   */
  event?: keyof HTMLElementEventMap;
  /**
   * 是否在复制前阻止默认行为（默认 false）
   */
  prevent?: boolean;
  /**
   * 是否在复制前停止冒泡（默认 false）
   */
  stop?: boolean;
}

export type CopyBindingValue = unknown | CopyBindingOptions;

const toText = (val: unknown): string => {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'bigint' || typeof val === 'boolean')
    return String(val);
  return String(val);
};

type CopyHandler = (e: Event) => void;

const resolveOptions = (value: CopyBindingValue): CopyBindingOptions => {
  if (value && typeof value === 'object' && 'text' in value) return value as CopyBindingOptions;
  return { text: value };
};

const buildHandler = (el: HTMLElement, value: CopyBindingValue): CopyHandler => {
  const opts = resolveOptions(value);
  return async (e) => {
    if (opts.prevent) e.preventDefault();
    if (opts.stop) e.stopPropagation();

    const raw = typeof opts.text === 'function' ? (opts.text as CopyTextGetter)() : opts.text;
    const text = toText(raw);

    try {
      await copyToClipboard(text);
      if (opts.success) {
        opts.success(text, el);
      } else {
        ElMessage.success('复制成功');
      }
    } catch (err) {
      opts.error?.(err, el);
    }
  };
};

const handlerMap = new WeakMap<HTMLElement, CopyHandler>();
const eventMap = new WeakMap<HTMLElement, keyof HTMLElementEventMap>();

export const vCopy: Directive<HTMLElement, CopyBindingValue> = {
  beforeMount(el, binding) {
    const opts = resolveOptions(binding.value);
    const event = (opts.event ?? 'click') as keyof HTMLElementEventMap;
    const handler = buildHandler(el, binding.value);

    handlerMap.set(el, handler);
    eventMap.set(el, event);

    el.addEventListener(event, handler);
  },
  updated(el, binding) {
    const prevHandler = handlerMap.get(el);
    const prevEvent = eventMap.get(el);

    const opts = resolveOptions(binding.value);
    const nextEvent = (opts.event ?? 'click') as keyof HTMLElementEventMap;
    const nextHandler = buildHandler(el, binding.value);

    if (prevHandler && prevEvent) el.removeEventListener(prevEvent, prevHandler);

    handlerMap.set(el, nextHandler);
    eventMap.set(el, nextEvent);

    el.addEventListener(nextEvent, nextHandler);
  },
  unmounted(el) {
    const handler = handlerMap.get(el);
    const event = eventMap.get(el);
    if (handler && event) el.removeEventListener(event, handler);
    handlerMap.delete(el);
    eventMap.delete(el);
  },
};
